import React from 'react';
import { segmentColor } from './CompareChart';
import { segmentStatistics, segmentKey as segmentKeyOf } from './compareSeries';

// The Describe summary box, in the same place, carrying what Compare adds:
// the totals across everything selected, then the same description per
// Segment. Numeric rows appear only when the categories are numbers — an
// average over labels would be an invented statistic.
const number = (value) => (Number.isInteger(value) ? value : Number(value).toFixed(2));

const Row = ({ label, value, muted }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
    <span style={{ color: '#5F6368' }}>{label}</span>
    <span style={{ color: muted ? '#5F6368' : '#202124', fontWeight: muted ? 400 : 500 }}>{value}</span>
  </div>
);

export default function CompareSummaryBox({ series, typeLabel, cells = [], mappings = [] }) {
  const statistics = series ? segmentStatistics(series) : [];
  const countLetter = series?.countLetter || 'n';
  // Answered comes from the returned cells, never from the drawn series. An
  // answer excluded from the typed calculation is still an answer, and
  // reporting 0 answered for a Segment that answered 46 times is simply wrong.
  const cellFor = (key) => cells.find(cell => segmentKeyOf(cell.mapping) === key);
  const answeredFor = (key) => Number(cellFor(key)?.answered_n) || 0;
  const excludedFor = (key) => Number(cellFor(key)?.calculation_ineligible_value_count) || 0;
  const answered = cells.reduce((sum, cell) => sum + (Number(cell.answered_n) || 0), 0);
  const excluded = cells.reduce((sum, cell) => sum + (Number(cell.calculation_ineligible_value_count) || 0), 0);
  // Eligible population is the deployment-scoped mapping count, which is a
  // fact about the Segment rather than about this question.
  const eligible = mappings.reduce((sum, mapping) => sum + (mapping.included_recipient_count || 0), 0);
  const hasNumbers = statistics.some(stat => stat.mean !== undefined);
  const indexOf = (key) => (series?.segments || []).findIndex(segment => segment.key === key);
  const statFor = (key) => statistics.find(stat => stat.key === key);
  const perSegment = mappings.map(mapping => {
    const key = segmentKeyOf(mapping);
    return { key, name: mapping.logical_segment_name || 'Unnamed Segment', stat: statFor(key) };
  });

  return (
    <div style={{
      backgroundColor: '#FAFBFC', border: '1px solid #E8EAED', borderRadius: 6,
      padding: 12, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#5F6368', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Summary
      </div>
      <Row label="Segments" value={mappings.length} />
      <Row label="Answered (n)" value={answered} />
      {countLetter === 'c' && <Row label="Categorized (c)" value={statistics.reduce((sum, stat) => sum + (stat.n || 0), 0)} />}
      {excluded > 0 && <Row label="Excluded from calculation" value={excluded} />}
      <Row label="Population" value={eligible} />
      <Row label="Type" value={typeLabel || 'Unavailable'} />

      {/* Driven by the SELECTION, not by the drawn series, so a Segment stays
          described even when nothing could be charted for it. */}
      {perSegment.length > 0 && (
        <>
          <div style={{ borderTop: '1px solid #E8EAED', margin: '4px 0 0' }} />
          <div style={{ fontSize: 11, fontWeight: 600, color: '#5F6368', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            By Segment
          </div>
          {perSegment.map(({ key, name, stat }) => {
            const index = indexOf(key);
            return (
              <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    aria-hidden="true"
                    style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, backgroundColor: segmentColor(index < 0 ? 0 : index) }}
                  />
                  <span style={{ color: '#202124', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {name}
                  </span>
                </div>
                <div style={{ paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
                  <Row label="n" value={answeredFor(key)} muted />
                  {countLetter === 'c' && stat && <Row label="c" value={stat.n} muted />}
                  {excludedFor(key) > 0 && <Row label="excluded" value={excludedFor(key)} muted />}
                  {stat?.mean !== undefined && <>
                    <Row label="mean" value={number(stat.mean)} muted />
                    <Row label="median" value={number(stat.median)} muted />
                    <Row label="σ" value={number(stat.sd)} muted />
                    <Row label="min / max" value={`${number(stat.min)} / ${number(stat.max)}`} muted />
                  </>}
                </div>
              </div>
            );
          })}
          {statistics.length > 0 && !hasNumbers && (
            <div style={{ fontSize: 11, color: '#9AA0A6' }}>
              These answers are labels, so they are described by counts rather than by an average.
            </div>
          )}
        </>
      )}
    </div>
  );
}
